package features.overtime.ui

import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import kotlinx.html.dom.append
import org.w3c.dom.HTMLElement
import features.teams.domain.Team
import AppState
import shared.ui.ModalManager
import features.overtime.handlers.approveOvertimeRequest
import features.overtime.handlers.rejectOvertimeRequest
import features.overtime.data.OvertimeRepository


object OvertimeTab {
    fun render(parent: DIV, team: Team) {
        parent.div {
            div("flex justify-between items-center mb-6") {
                h3("text-2xl font-bold") { +"Overtime Requests" }
                button(classes = "bg-brand-cyan hover:bg-brand-green text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2") {
                    onClickFunction = { ModalManager.show("request-overtime-modal") }
                    span { +"⏱️" }
                    +"Request Overtime"
                }
            }
            
            // Loading container
            div {
                id = "overtime-list-container"
                div("text-center py-12") {
                    div("animate-spin rounded-full h-12 w-12 border-b-2 border-brand-cyan mx-auto") {}
                    p("text-slate-400 mt-4") { +"Loading overtime requests..." }
                }
            }
        }
        
        // Load overtime requests from Firebase
        loadOvertimeRequests(team)
    }
    
    private fun loadOvertimeRequests(team: Team) {
        OvertimeRepository.getOvertimeRequests(team.id)
            .then { requests ->
                console.log("📋 Loaded ${requests.size} overtime requests")
                renderOvertimeList(team, requests)
            }
            .catch { error ->
                console.error("❌ Failed to load overtime requests:", error)
                renderError()
            }
    }
    
    private fun renderOvertimeList(team: Team, requests: List<features.overtime.domain.OvertimeRequest>) {
        val container = kotlinx.browser.document.getElementById("overtime-list-container") as? HTMLElement
        container?.innerHTML = ""
        
        if (container != null) {
            container.append {
                if (requests.isEmpty()) {
                    div("text-center py-12") {
                        img(src = "/images/empty-states/no-overtime.png", alt = "No overtime requests") {
                            classes = setOf("w-64", "h-64", "mx-auto", "mb-6")
                        }
                        h3("text-xl font-bold mb-2") { +"No Overtime Requests Yet" }
                        p("text-slate-400") { +"Submit your first overtime request to get started" }
                    }
                } else {
                    // Wrapper with horizontal scroll for mobile
                    div("bg-dark-800 rounded-2xl border border-dark-700 overflow-x-auto") {
                        table("min-w-max w-full text-left border-collapse") {
                            thead {
                                tr("bg-dark-900/50 text-slate-400 border-b border-dark-700") {
                                    th { classes = setOf("p-4", "font-medium"); +"Date" }
                                    th { classes = setOf("p-4", "font-medium"); +"Member" }
                                    th { classes = setOf("p-4", "font-medium"); +"Start" }
                                    th { classes = setOf("p-4", "font-medium"); +"End" }
                                    th { classes = setOf("p-4", "font-medium"); +"Hours" }
                                    th { classes = setOf("p-4", "font-medium"); +"Reason" }
                                    th { classes = setOf("p-4", "font-medium"); +"Status" }
                                    th { classes = setOf("p-4", "font-medium", "text-right"); +"Actions" }
                                }
                            }
                            tbody {
                                requests.forEach { req ->
                                    val member = AppState.members[team.id]?.find { it.id == req.userId }
                                    val statusClass = when (req.status) {
                                        "Approved" -> "bg-emerald-500/10 text-emerald-400"
                                        "Pending" -> "bg-amber-500/10 text-amber-400"
                                        "Rejected" -> "bg-red-500/10 text-red-400"
                                        else -> "bg-slate-500/10 text-slate-400"
                                    }
                                    
                                    tr("hover:bg-dark-700/30 transition border-b border-dark-700 last:border-0") {
                                        td("p-4 text-white font-medium") { +req.date }
                                        td("p-4 text-slate-300") { +(member?.name ?: "Unknown") }
                                        td("p-4 text-slate-300") { +req.startTime }
                                        td("p-4 text-slate-300") { +req.endTime }
                                        td("p-4 text-slate-300 font-bold") { +req.hours }
                                        td("p-4 text-slate-400 max-w-xs truncate") { 
                                            attributes["title"] = req.reason
                                            +req.reason 
                                        }
                                        td("p-4") {
                                            span("px-2 py-1 rounded text-xs font-bold $statusClass") {
                                                +req.status
                                            }
                                        }
                                        td("p-4 text-right") {
                                            if (req.status == "Pending") {
                                                // Only show action buttons for team owners
                                                val currentUser = AppState.currentUser
                                                val isOwner = currentUser != null && team.ownerId == currentUser.id
                                                
                                                if (isOwner) {
                                                    div("flex gap-2 justify-end") {
                                                        button(classes = "px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition text-xs font-bold border border-emerald-500/30") {
                                                            onClickFunction = { approveOvertimeRequest(team.id, req.id) }
                                                            +"✓ Approve"
                                                        }
                                                        button(classes = "px-3 py-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition text-xs font-bold border border-red-500/30") {
                                                            onClickFunction = { rejectOvertimeRequest(team.id, req.id) }
                                                            +"✕ Reject"
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    
    private fun renderError() {
        val container = kotlinx.browser.document.getElementById("overtime-list-container") as? HTMLElement
        container?.innerHTML = ""
        
        if (container != null) {
            container.append {
                div("text-center py-12") {
                    p("text-red-400 text-xl mb-2") { +"❌ Failed to load overtime requests" }
                    p("text-slate-400") { +"Please refresh the page to try again" }
                }
            }
        }
    }
}
